import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../utils/AuthContext';
import './Navbar.css';
import logo from '../../../../styles/logo_lms.png';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import NotificationsPanel from '../../../Notifications/NotificationsPanel';
import SmartImage from '../SmartImage/SmartImage';

const Navbar = () => {
    const { user, isAuthenticated, isOrganization, isStudent, isTeacher, isAdmin, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const dropdownRef = useRef(null);
    const mobileMenuRef = useRef(null);
    const userSelectorRef = useRef(null);

    // Закрытие dropdown при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                userSelectorRef.current && !userSelectorRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Закрытие мобильного меню при клике вне его
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileMenuRef.current &&
                !mobileMenuRef.current.contains(event.target) &&
                !event.target.closest('.mobile-menu-btn')) {
                setMobileMenuOpen(false);
            }
        };

        if (mobileMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [mobileMenuOpen]);

    // Закрытие меню при изменении роута
    useEffect(() => {
        setMobileMenuOpen(false);
        setDropdownOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        try {
            await logout();
            setDropdownOpen(false);
            setMobileMenuOpen(false);
            navigate('/');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const getNavItems = () => {
        const items = [
            { path: '/news', label: 'Новости' },
            { path: '/projects', label: 'Проекты' },
            { path: '/organizations', label: 'Организации' },
        ];

        if (isAuthenticated) {
            items.push({ path: '/tracks', label: 'Треки' });
        }

        return items;
    };

    // Создание элемента меню
    const createMenuItem = (item, isMobile = false) => {
        const iconPaths = {
            news: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z',
            projects: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
            tracks: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
            profile: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
            settings: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
            notifications: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
            organization: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
            moderation: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
            admin: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
            login: 'M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1',
            register: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
            logout: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
        };

        const icon = item.icon || iconPaths[item.key] || '';

        return (
            <Link
                key={item.path}
                to={item.path}
                className={isMobile ? "mobile-menu-item" : "dropdown-item"}
                onClick={() => {
                    setDropdownOpen(false);
                    setMobileMenuOpen(false);
                }}
            >
                {icon && (
                    <svg className={isMobile ? "mobile-menu-icon" : "dropdown-icon"} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                    </svg>
                )}
                {item.label}
            </Link>
        );
    };

    // Получаем все ссылки для dropdown меню
    const getDropdownLinks = () => {
        const links = [];

        // Разделитель
        links.push(<div key="divider-main" className="dropdown-divider" />);

        // Для авторизованных пользователей
        if (isAuthenticated) {
            // Профиль и настройки
            links.push(createMenuItem({ path: `/profile/${user.slug || user.id}`, label: 'Мой профиль', key: 'profile' }, false));
            links.push(createMenuItem({ path: '/notifications', label: 'Уведомления', key: 'notifications' }, false));

            // Дополнительные ссылки для организаций
            if (isOrganization()) {
                links.push(createMenuItem({ path: '/organization/dashboard', label: 'Панель организации', key: 'organization' }, false));
            }

            // Только для админов
            if (isAdmin()) {
                links.push(createMenuItem({ path: '/admin', label: 'Админ панель', key: 'admin' }, false));
            }

            links.push(<div key="divider-auth" className="dropdown-divider" />);

            // Кнопка выхода
            links.push(
                <button
                    key="logout"
                    className="dropdown-item"
                    onClick={handleLogout}
                >
                    <svg className="dropdown-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Выйти
                </button>
            );
        } else {
            // Для гостей
            links.push(<div key="divider-guest" className="dropdown-divider" />);
            links.push(createMenuItem({ path: '/login', label: 'Войти', key: 'login' }, false));
            links.push(createMenuItem({ path: '/register', label: 'Зарегистрироваться', key: 'register' }, false));
        }

        return links;
    };

    // Получаем ссылки для мобильного меню
    const getMobileMenuLinks = () => {
        const links = [];

        // Основные навигационные ссылки
        getNavItems().forEach(item => {
            links.push(createMenuItem(item, true));
        });

        // Разделитель
        links.push(<div key="divider-main-mobile" className="mobile-menu-divider" />);

        // Для авторизованных пользователей
        if (isAuthenticated) {
            links.push(createMenuItem({ path: `/profile/${user.slug || user.id}`, label: 'Мой профиль', key: 'profile' }, true));
            links.push(createMenuItem({ path: '/notifications', label: 'Уведомления', key: 'notifications' }, true));

            if (isOrganization()) {
                links.push(createMenuItem({ path: '/organization/dashboard', label: 'Панель организации', key: 'organization' }, true));
            }

            if (isAdmin()) {
                links.push(createMenuItem({ path: '/admin', label: 'Админ панель', key: 'admin' }, true));
            }

            // Кнопка выхода
            links.push(
                <button
                    key="logout-mobile"
                    className="mobile-menu-item mobile-logout-btn"
                    onClick={handleLogout}
                >
                    <svg className="mobile-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Выйти
                </button>
            );
        } else {
            links.push(createMenuItem({ path: '/login', label: 'Войти', key: 'login' }, true));
            links.push(createMenuItem({ path: '/register', label: 'Зарегистрироваться', key: 'register' }, true));
        }

        return links;
    };

    // Отображаем роль пользователя рядом с именем
    const getUserRoleLabel = () => {
        if (isAdmin()) return 'Админ';
        if (isTeacher()) return 'Преподаватель';
        if (isOrganization()) return 'Организация';
        if (isTeacher()) return 'Преподаватель';
        if (isStudent()) return 'Студент';
        return 'Пользователь';
    };

    return (
        <div className="navbar-scope">
            <nav className="navbar">
                <div className="navbar-container">
                    {/* Логотип */}
                    <Link to="/" className="navbar-logo">
                        <SmartImage src={logo} className="logo-icon" alt="Биржа проектов" />
                        <span className="logo-text">Биржа проектов</span>
                    </Link>

                    {/* Кнопки для мобильного хедера: колокольчик (если авторизован) + тема (только когда меню закрыто) */}
                    {!mobileMenuOpen && (
                        <div className="mobile-header-controls">
                            {isAuthenticated && <NotificationsPanel />}
                            <ThemeToggle />
                        </div>
                    )}

                    {/* Кнопка мобильного меню */}
                    <button
                        className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
                        onClick={toggleMobileMenu}
                        aria-label="Меню"
                    >
                        <span className="burger-line"></span>
                        <span className="burger-line"></span>
                        <span className="burger-line"></span>
                    </button>

                    {/* Навигационные ссылки для десктопа */}
                    <div className="navbar-nav">
                        {getNavItems().map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`nav-link ${location.pathname === item.path ? 'active' : ''
                                    }`}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>

                    {/* Блок авторизации/пользователя для десктопа */}
                    <div className="navbar-auth">
                        <div className="navbar-controls">
                            <ThemeToggle />
                            {isAuthenticated && <NotificationsPanel />}
                        </div>
                        {!isAuthenticated ? (
                            <div className="auth-buttons">
                                <Link to="/login" className="auth-button auth-login">
                                    Войти
                                </Link>
                                <Link to="/register" className="auth-button auth-register">
                                    Зарегистрироваться
                                </Link>
                            </div>
                        ) : (
                            <div className="navbar-user">
                                <div
                                    className="navbar-user-selector"
                                    onClick={toggleDropdown}
                                    ref={userSelectorRef}
                                >
                                    <div className="user-avatar">
                                        <SmartImage 
                                            src={user?.avatar} 
                                            alt={user?.name} 
                                            className="avatar-image" 
                                            fallbackType="user"
                                        />
                                    </div>
                                    <div className="user-info">
                                        <span className="user-name">{user?.name}</span>
                                        <span className="user-role-label">{getUserRoleLabel()}</span>
                                    </div>
                                </div>

                                {/* Dropdown меню для десктопа */}
                                <div className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`} ref={dropdownRef}>
                                    {getDropdownLinks()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            {/* Мобильное меню */}
            <div
                className={`mobile-menu ${mobileMenuOpen ? 'show' : ''}`}
                ref={mobileMenuRef}
            >
                <div className="mobile-menu-header">
                    {isAuthenticated ? (
                        <div className="mobile-user-info">
                            <div className="mobile-user-avatar">
                                <SmartImage
                                    src={user?.avatar}
                                    alt={user?.name}
                                    className="mobile-avatar-image"
                                    fallbackType="user"
                                />
                            </div>
                            <div className="mobile-user-details">
                                <div className="mobile-user-name">{user?.name}</div>
                                <div className="mobile-user-role">{getUserRoleLabel()}</div>
                            </div>
                        </div>
                    ) : (
                        <div className="mobile-user-info">
                        </div>
                    )}
                    <div className="mobile-header-actions-container">
                        <ThemeToggle />
                        {isAuthenticated && <NotificationsPanel />}
                    </div>
                </div>

                <div className="mobile-menu-content">
                    {getMobileMenuLinks()}
                </div>
            </div>

            {/* Overlay для мобильного меню */}
            <div
                className={`mobile-overlay ${mobileMenuOpen ? 'show' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
            />

            {/* Отступ для фиксированного навбара */}
            <div className="navbar-spacer" />
        </div>
    );
};

export default Navbar;